import React from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Link2, X } from 'lucide-react';

/**
 * Visible "you came here via a deep-link" badge.
 *
 * Mounted near the top of any list page that honors URL params. Renders
 * itself as a single chip per detected param so the user always sees what
 * pre-filtered the view, with a one-click reset that strips the params and
 * also calls the page-supplied `onReset` to clear local state.
 *
 * Usage:
 *   <UrlFilterChip
 *     params={[
 *       { key: 'status', label: 'Zahlungsstatus', valueLabels: { open: 'Offen' } },
 *     ]}
 *     onReset={() => setStatFilter('all')}
 *   />
 */
export default function UrlFilterChip({ params = [], onReset }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const active = params
    .map((p) => {
      const v = searchParams.get(p.key);
      if (!v) return null;
      const display = (p.valueLabels && p.valueLabels[v]) || v;
      return { ...p, value: v, display };
    })
    .filter(Boolean);

  if (active.length === 0) return null;

  const handleClear = () => {
    // Strip our known keys from the URL while preserving anything else
    const next = new URLSearchParams(searchParams);
    params.forEach((p) => next.delete(p.key));
    const qs = next.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    if (onReset) onReset();
  };

  return (
    <div
      className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-red/5 border border-brand-red/20 px-3 py-1.5 text-xs text-brand-red"
      data-testid="url-filter-chip"
    >
      <Link2 className="w-3.5 h-3.5 text-brand-red" />
      <span className="font-medium">Filter aus URL:</span>
      <span className="flex items-center gap-1">
        {active.map((a, i) => (
          <span key={a.key} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-brand-red/30">·</span>}
            <span className="text-slate-600">{a.label}:</span>
            <span className="font-semibold text-brand-red">{a.display}</span>
          </span>
        ))}
      </span>
      <button
        type="button"
        onClick={handleClear}
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-white border border-brand-red/20 px-2 py-0.5 hover:bg-brand-red/10 transition-colors text-brand-red"
        data-testid="url-filter-chip-reset"
        title="Filter zurücksetzen"
      >
        <X className="w-3 h-3" />
        zurücksetzen
      </button>
    </div>
  );
}
